"use client";

import { useState } from "react";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import { ManyPanel } from "@/components/tools/physics-fields/coulomb-many-panel";
import {
  PairPanel,
  PointFieldPanel,
} from "@/components/tools/physics-fields/coulomb-pair-panels";
import {
  type SigChoice,
  SigFigsField,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const COULOMBS_LAW_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modePair: "Force between two charges",
  modeMany: "Net force and field from several charges",
  modeField: "Electric field of one charge",
  exampleLabel: "Start from an example",
  custom: "Custom values",
  pairExamples: {
    micro: "Two +1 µC charges 1 m apart",
    nano: "+2 nC and −3 nC, 5 cm apart",
    hydrogen: "Hydrogen atom: proton and electron at the Bohr radius",
  },
  manyExamples: {
    triangle: "A +1 µC test charge between +3 µC and −2 µC",
    bisector: "Two +1 µC charges: field above their midpoint",
    dipole: "A ±1 nC dipole: field on its axis",
  },
  fieldExamples: {
    micro: "A 1 µC charge, field at 0.5 m",
    hydrogen: "A proton, field at the Bohr radius",
  },
  mediumLabel: "Medium between the charges",
  mediumNames: {
    vacuum: "Vacuum (εᵣ = 1)",
    air: "Air (εᵣ ≈ 1.00059)",
    water: "Water at 20 °C (εᵣ ≈ 80.2)",
  },
  permittivity: "Relative permittivity εᵣ",
  permittivityHint:
    "A dimensionless number: 1 in vacuum. Other media weaken the force by the factor εᵣ.",
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  charge1: "Charge q₁",
  charge2: "Charge q₂",
  chargeHint:
    "Negative for a negative charge: −1.6e-19, or −1 in elementary charges (e).",
  distance: "Distance r between the charges",
  distanceHint: "Centre to centre, greater than zero.",
  force: "Force F (+ repels, − attracts)",
  forceHint:
    "Positive for repulsion between like charges, negative for attraction.",
  forceShort: "Force",
  charge1Short: "Charge q₁",
  charge2Short: "Charge q₂",
  distanceShort: "Distance",
  fieldShort: "Field E",
  chargeShort: "Charge",
  chargeMagnitude: "Size of the charge |q|",
  magnitudeHint: "Only the size: choose its sign below.",
  chargeSign: "Sign of the charge",
  positive: "Positive",
  negative: "Negative",
  distanceToPoint: "Distance r from the charge",
  fieldStrength: "Field strength E",
  forceSigned: "Force F",
  forceMagnitude: "Force magnitude",
  theForceIs: "The force is",
  repulsive: "Repulsive (like charges)",
  attractive: "Attractive (opposite charges)",
  noForce: "Zero",
  direction: "Direction of the field",
  awayFromCharge: "Away from the charge",
  towardCharge: "Toward the charge",
  potential: "Electric potential V",
  sourcesHeading: "Source charges and their positions",
  chargeColumn: "charge",
  xColumn: "x",
  yColumn: "y",
  sourceField: "Charge {n}: {field}",
  chargeUnit: "Unit for the charges",
  positionUnit: "Unit for the positions",
  removeCharge: "Remove charge {n}",
  remove: "Remove",
  addCharge: "Add a charge",
  pointX: "Field point x",
  pointY: "Field point y",
  testCharge: "Test charge q₀ at the point (optional)",
  testChargeHint: "Gives the force on it. Leave empty for the field alone.",
  fieldMagnitude: "Field strength |E|",
  fieldDirection: "Field direction (from +x)",
  forceDirection: "Force direction (from +x)",
  contributionCaption: "The field of each charge at the point (N/C).",
  colCharge: "Charge",
  colPosition: "Position",
  colDistance: "Distance to the point",
  colEx: "Eₓ",
  colEy: "E_y",
  colMagnitude: "|E|",
  sigFigs: "Significant figures",
  sigFigsMessage:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  working: "The working",
  pairLabel: "Two point charges and the force between them",
  pairCaption:
    "Like charges push apart and opposite charges pull together, with equal and opposite forces.",
  pairDescription:
    "Two circles marked with plus or minus signs, labelled q1 and q2, a distance r apart, each with an arrow labelled F pointing away from the other charge for repulsion or toward it for attraction.",
  radialLabel: "Electric field lines around a point charge",
  radialCaption:
    "The field points away from a positive charge and toward a negative one, and weakens as 1/r².",
  radialDescription:
    "A circle marked with a plus or minus sign at the centre with eight arrows around it pointing outward for a positive charge or inward for a negative charge, and a dashed line labelled r.",
  planeLabel: "Charges, the field point and the field at it",
  planeCaption:
    "Grey arrows are each charge's field at P; the black arrow is their vector sum. A sketch: positions are fitted to the picture, arrow lengths follow field strength.",
  planeDescription:
    "Charges drawn as circles marked plus or minus on a plane, a green square at the field point P, thin grey arrows from P for each charge's field and a thick black arrow for the total field.",
  errorMissing:
    "Fill in every box except the one you are solving for. Scientific notation such as 1.6e-19 works, and negative charges take a minus sign.",
  errorPositive:
    "The distance and the relative permittivity must be greater than zero.",
  errorZeroCharge:
    "A charge of zero exerts no force, so the other charge or the distance cannot be found from it. Use a non-zero charge.",
  errorZeroForce:
    "A force of zero would need an infinite distance. Enter a non-zero force.",
  errorSign:
    "That force has the wrong sign for these charges: like charges repel (positive force) and opposite charges attract (negative force).",
  errorCoincident:
    "The field point sits on a charge, where the field is infinite. Move the point away from every charge.",
  errorRange:
    "Those numbers are too large or too small to calculate. Check the units.",
  assumptions:
    "Assumes static point charges, or uniformly charged spheres measured centre to centre, in a uniform medium. Moving charges, conductors that rearrange their charge, and magnetic effects are outside the model. The constant kₑ = 8.9875517862 × 10⁹ N·m²/C² is 1/(4πε₀) with the CODATA 2022 value of ε₀.",
} as const;

type Mode = "pair" | "many" | "field";

export default function CoulombsLawCalculatorWidget() {
  const strings = useToolUi(COULOMBS_LAW_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("pair");
  const [sigChoice, setSigChoice] = useState<SigChoice>("4");
  const sig = Number(sigChoice);

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="cl-mode"
        onChange={setMode}
        options={[
          { value: "pair", label: strings.modePair },
          { value: "many", label: strings.modeMany },
          { value: "field", label: strings.modeField },
        ]}
        value={mode}
      />
      {mode === "pair" ? <PairPanel sig={sig} strings={strings} /> : null}
      {mode === "many" ? <ManyPanel sig={sig} strings={strings} /> : null}
      {mode === "field" ? (
        <PointFieldPanel sig={sig} strings={strings} />
      ) : null}
      <SigFigsField
        format={fmt}
        id="cl-sig"
        label={strings.sigFigs}
        locale={locale}
        message={strings.sigFigsMessage}
        onChange={setSigChoice}
        value={sigChoice}
      />
      <Notice>{strings.assumptions}</Notice>
    </>
  );
}
